import React, {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {fetchRoomsForPresentation} from '../../../../service/room.service';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';

const RESERVED_FILTER_KEYS = new Set(['roomId', 'groupIdentification', 'specificIdentification', 'from', 'to']);

type FilterQuery = Record<string, string | string[] | undefined>;

interface PdfReportFiltersHeaderProps {
    filterQuery: FilterQuery;
}

function formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
}

function PdfReportFiltersHeader({filterQuery}: PdfReportFiltersHeaderProps): JSX.Element | null {
    const {t} = useTranslation();
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const presId = selectedPresentation._id;

    const {data: rooms} = useQuery(QUERY_KEYS.roomsForPresentation.detail(presId), () => fetchRoomsForPresentation(presId), {
        enabled: !!presId && !!filterQuery.roomId,
    });

    const selectedRoom = useMemo(() => {
        if (!filterQuery.roomId || !rooms) return undefined;
        return rooms.find((room) => room._id === filterQuery.roomId);
    }, [filterQuery.roomId, rooms]);

    const filterItems = useMemo(() => {
        const items: {label: string; value: string}[] = [];

        if (filterQuery.from) {
            items.push({label: t('reportsPage.fromDate'), value: formatDate(String(filterQuery.from))});
        }
        if (filterQuery.to) {
            items.push({label: t('reportsPage.toDate'), value: formatDate(String(filterQuery.to))});
        }

        if (filterQuery.roomId) {
            items.push({
                label: t('reportPage.filterRoomsTitle'),
                value: selectedRoom?.roomName || String(filterQuery.roomId),
            });

            if (filterQuery.groupIdentification) {
                items.push({
                    label: selectedRoom?.identification?.groupIdentificationLabel || t('reportPage.pdfGroupIdentification'),
                    value: String(filterQuery.groupIdentification),
                });
            }

            if (filterQuery.specificIdentification) {
                items.push({
                    label: selectedRoom?.identification?.specificIdentificationLabel || t('reportPage.pdfSpecificIdentification'),
                    value: String(filterQuery.specificIdentification),
                });
            }
        }

        Object.entries(filterQuery).forEach(([key, value]) => {
            if (RESERVED_FILTER_KEYS.has(key) || value === undefined || value === '') return;
            const displayValue = Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value);
            if (!displayValue) return;
            items.push({label: key, value: displayValue});
        });

        return items;
    }, [filterQuery, selectedRoom, t]);

    if (!filterItems.length) return null;

    return (
        <div className="p-presReport__pdfFilters">
            <div className="p-presReport__card_title">{selectedPresentation.name}</div>
            <div className="p-presReport__card p-presReport__pdfFilters_card">
                <div className="p-presReport__pdfFilters_heading">{t('reportPage.pdfAppliedFilters')}</div>
                <dl className="p-presReport__pdfFilters_list">
                    {filterItems.map((item) => (
                        <div key={`${item.label}-${item.value}`} className="p-presReport__pdfFilters_item">
                            <dt>{item.label}</dt>
                            <dd>{item.value}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
}

export default PdfReportFiltersHeader;
